import React from 'react';
import {
  CurrencyCodes,
  CURRENCY_CODES,
  CURRENCY_SYMBOLS,
} from 'common/configs/currency';
import styled from 'styled-components';

export const CurrencyContainer = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
`;

export const CurrencySymbol = styled.span`
  text-align: left;
  margin-right: 0.25rem;
`;

export const CurrencyValue = styled.span<{ value?: number }>`
  color: ${({ value, theme }) => {
    if (value > 0) return theme.primary;
    if (value < 0) return theme.danger;
    return undefined;
  }};
  text-align: right;
`;

export interface CurrencyFomattingOptions {
  symbol?: 'symbol' | 'code' | false | null;
  decimalPlaces?: number;
  localeString?: boolean;
  colour?: boolean;
}

export const symbolRegex = [
  ...Object.values(CURRENCY_CODES),
  ...Object.values(CURRENCY_SYMBOLS),
]
  .map((s) => s.replaceAll('$', '\\$').replaceAll('.', '\\.'))
  .join('|');
export const decimalRegex = '\\d+\\.?\\d*';
// export const lhsCurrencyRegex = new RegExp(
//   `-?(${symbolRegex})-?${decimalRegex}`
// );
// export const rhsCurrencyRegex = new RegExp(`-?${decimalRegex}(${symbolRegex})`);
export const isCurrencyString = (s: string): boolean => {
  const cleanS = s.replaceAll(/[\s,]+/g, '');
  // More than one - symbol like in dates
  if (cleanS.match(/-/g)?.length > 1) return false;
  // More than one . symbol like in IPs
  if (cleanS.match(/\./g)?.length > 1) return false;
  // Get rid of matches like totus3etGBP that have currency code & number
  // but have other characters as well.
  if (cleanS.replace(new RegExp(symbolRegex), '').match(/[A-Za-z]+/))
    return false;
  return !!(cleanS.match(symbolRegex) && cleanS.match(decimalRegex));
};

export class Currency {
  value?: number;
  code?: string;
  symbol?: string;

  constructor(value?: number | string | null, code?: string) {
    // Round to 6dp to ignore very small values usually from floating point maths errors
    value = typeof value === "number" ? Math.round(value*10**6) / 10**6 : value
    this.value =
      value?.toString()?.match(/[0-9.-]/g) &&
      parseFloat(
        value
          .toString()
          .match(/[0-9.-]/g)
          .join('')
      );

    if (isNaN(this.value)) this.value = undefined;

    let _code = code && code.toUpperCase();
    // Some times bacon values have the currency code in the string
    if (!_code && value != null) {
      const extracted_code = value
        ?.toString()
        ?.match(/[a-zA-Z]/g)
        ?.join('')
        .toUpperCase();
      if (extracted_code in CurrencyCodes)
        _code = extracted_code?.toUpperCase();
    }
    if (_code in CURRENCY_CODES) {
      this.symbol = _code;
      this.code = CURRENCY_CODES[_code];
    } else {
      this.code = _code;
      this.symbol = CURRENCY_SYMBOLS[_code] || _code;
    }
  }

  _defaultFormatOptions(format?: CurrencyFomattingOptions) {
    return {
      symbol: 'symbol',
      decimalPlaces: 2,
      localeString: true,
      colour: true,
      ...(format || {}),
    };
  }

  _formatValue(format?: CurrencyFomattingOptions) {
    const _format = this._defaultFormatOptions(format);
    let value = '-';
    if (this.value != null) {
      const _valueInt =
        Math.sign(this.value) * Math.floor(Math.abs(this.value));
      const _valueDec = this.value - _valueInt;

      value = _format.localeString
        ? _valueInt.toLocaleString()
        : _valueInt.toString();

      if (_format.decimalPlaces > 0) {
        value += '.';
        value += _valueDec.toFixed(_format.decimalPlaces).split('.')[1];
      }
    }
    return value;
  }

  toElement(format?: CurrencyFomattingOptions) {
    const _format = this._defaultFormatOptions(format);
    return (
      <CurrencyContainer className="currency">
        <CurrencySymbol className="symbol">
          {_format.symbol === 'symbol'
            ? this.symbol
            : _format.symbol === 'code'
            ? this.code
            : ''}
        </CurrencySymbol>
        <CurrencyValue
          value={_format.colour ? this.value : undefined}
          className="value"
        >
          {this._formatValue(format)}
        </CurrencyValue>
      </CurrencyContainer>
    );
  }

  toString(format?: CurrencyFomattingOptions) {
    const _format = this._defaultFormatOptions(format);
    return `${
      _format.symbol === 'symbol'
        ? this.symbol
        : _format.symbol === 'code'
        ? this.code + ' '
        : ''
    }${this._formatValue(format)}`;
  }

  valueOf() {
    return this.value;
  }
}
